/**
 * Talk-to-your-data: the chat page over the Priority mirror.
 *
 * The only user-facing surface of the agent (app/agent/chat.py). Consumes
 * POST /api/v1/chat/talk as an SSE stream, via PCMAdapter.postTalkStream
 * (data-adapter.js) -- call() cannot be reused here because the response is
 * a stream of frames, not one JSON body.
 *
 * Event shapes (see app/agent/chat.py, the source of truth -- do not trust
 * a stale copy of this comment over it):
 *   {type:'text', text}                    -- streamed delta, append
 *   {type:'tool', name, args, error?}      -- a tool call; error is set iff
 *                                              the tool raised, and never
 *                                              suppresses the call itself
 *   {type:'rewind', chars}                 -- (runtime=pi) drop the last `chars`
 *                                              Unicode CODE POINTS of streamed
 *                                              text; never UTF-16 units
 *   {type:'notice', text}                  -- (runtime=pi) one muted line
 *   {type:'done'}                          -- terminal
 *   {type:'error', message}                -- terminal; the answer stopped
 *                                              short (MAX_TURNS exhausted, or
 *                                              a provider failure the router
 *                                              caught) -- rendered as its own
 *                                              'stream-error' part, never
 *                                              folded into ordinary prose, so
 *                                              a truncated answer never reads
 *                                              as a finished one
 *
 * A missing LLM key never reaches the stream: resolve_agent_key() 400s the
 * plain POST before StreamingResponse opens (app/routers/chat.py), so it
 * surfaces as an HTTP error from postTalkStream, not as an SSE event.
 *
 * A presented SQL call also carries an artifact built from its returned
 * rows. That snapshot is saved with its tool part and rendered directly;
 * legacy markdown tables and chart fences still render as before.
 *
 * History (spec 2026-09-05 §6): this page still owns the running
 * conversation. A conversation row is created on the FIRST send, and after
 * every stream closes -- success, terminal error or transport failure --
 * the render model `turns` is PUT to /api/v1/chat/conversations/{id}.
 * Loading one rebuilds the wire history with wireHistory(). Two stated
 * ceilings: a tab closed mid-stream loses that one reply, and two browsers
 * on one conversation are last-write-wins.
 *
 * Priority values render with dir="auto" PER ELEMENT, same as form-view.jsx
 * -- never dir="rtl" on a container of mixed content, which would move the
 * punctuation of the LTR parts too.
 *
 * Exposed as window.ChatPage.
 */
import { wireHistory, assistantContent, titleFrom, applyChatEvent, CHAT_EVENT_KINDS } from './chat-blocks.mjs';
import {
  Canvas, CanvasBlock, blockAt, canvasTitle, chatCls, ChatMessage,
} from './chat-render.jsx';
import { TasksRail, TaskPanel } from './chat-tasks.jsx';
import { TASK_POLL_MS, scopedTaskParams } from './chat-tasks-logic.mjs';
import { RUN_KEY, rememberedEnv, rememberEnv, pickEnv } from './workbench-logic.mjs';
import { talkEnv, choiceByMirrorEnv, choiceLabel, pickPending } from './company-pick.mjs';
import { confirmAction } from './feedback.jsx';
import {DataDashboards, SaveToDashboard} from './data-dashboards.jsx';

const { useState, useRef, useEffect, useCallback, useMemo } = React;


// Four example questions for the empty state. Static copy, not data:
// clicking one only fills the composer. Colours are the module palette's
// first four hues, chosen for variety, not meaning.
const SUGGESTIONS = [
  ['Which customers placed no orders in the last 90 days?', 'איזה לקוחות לא הזמינו כלום ב-90 הימים האחרונים?', '#0284c7'],
  ['Serials produced this year, per month -- as a chart', 'כמה סריאלים ייצרנו השנה, לפי חודש — כתרשים', '#059669'],
  ['Open invoices over 60 days, by agent', 'חשבוניות פתוחות מעל 60 יום לפי סוכן', '#b45309'],
  ['Purchase invoices by supplier, top 10', 'חשבוניות רכש לפי ספק, 10 הגדולים', '#db2777'],
];

// ---------- the rail ----------
function relTime(iso, he) {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 60) return he ? `לפני ${mins} דק׳` : `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return he ? `לפני ${hrs} שע׳` : `${hrs}h ago`;
  return he ? `לפני ${Math.round(hrs / 24)} ימים` : `${Math.round(hrs / 24)}d ago`;
}

function ConvRail({
  convs, activeId, disabled, lang, onSelect, onNew, onDelete,
  tasks, tasksError, activeTaskId, canCreate, onSelectTask, noCompany,
}) {
  const he = lang === 'he';
  return (
    <>
      <nav className="chat-rail" aria-label={he ? 'שיחות' : 'Conversations'}>
        <button type="button" className="btn primary chat-rail-new" disabled={disabled} onClick={onNew}>
          {he ? '+ שיחה חדשה' : '+ New chat'}
        </button>
        <div className="nav-section chat-rail-label">{he ? 'שיחות' : 'Conversations'}</div>
        {convs.length === 0 && <div className="chat-rail-empty">{noCompany ? (he ? 'אין חברה זמינה, הרשימה לא נטענה' : 'No company available; the list was not loaded') : (he ? 'אין שיחות שמורות' : 'No saved conversations')}</div>}
        {convs.map((c) => (
          <div key={c.id} className={chatCls('chat-conv', c.id === activeId && 'active')}
               aria-disabled={disabled}>
            <button type="button" className="chat-conv-open" disabled={disabled} onClick={() => onSelect(c.id)}>
              <span className="chat-conv-title" dir="auto">{c.title || (he ? 'ללא כותרת' : 'Untitled')}</span>
              <span className="chat-conv-time">{relTime(c.updated_at, he)}</span>
            </button>
            <button type="button" className="chat-conv-del" aria-label={he ? 'מחק' : 'Delete'} disabled={disabled}
                    onClick={() => onDelete(c.id)}>×</button>
          </div>
        ))}
        {tasks && <TasksRail tasks={tasks} error={tasksError} activeTaskId={activeTaskId} canCreate={canCreate} lang={lang} onSelect={onSelectTask}/>}
      </nav>
      {/* Narrow screens: the same list as a select (atlas.css hides one or
          the other), with its own delete button -- the rail's per-row
          delete has no equivalent here, so at narrow widths it was
          unreachable at ANY width, not just by keyboard. Same handler,
          same confirm, applied to whichever conversation the select holds. */}
      {/* Narrow width used to collapse the whole rail into ONE select whose first
          option was "+ New chat". That hid both affordances at once: history did
          not look like history, and the only way to start a fresh conversation was
          to pick an option out of a dropdown. Below 1100px the rail is display:none,
          so this row IS the entire history UI -- it needs a real button and a label. */}
      <div className="chat-rail-select">
        <button type="button" className="btn primary btn-sm" disabled={disabled} onClick={onNew}>
          {he ? '+ שיחה חדשה' : '+ New chat'}
        </button>
        <select className="select" value={activeId == null ? '' : String(activeId)} disabled={disabled}
                aria-label={he ? 'שיחות שמורות' : 'Saved conversations'}
                onChange={(e) => { if (e.target.value !== '') onSelect(Number(e.target.value)); }}>
          <option value="">
            {convs.length
              ? (he ? `— ${convs.length} שיחות שמורות —` : `— ${convs.length} saved —`)
              : (he ? '— אין שיחות שמורות —' : '— No saved conversations —')}
          </option>
          {convs.map((c) => <option key={c.id} value={String(c.id)}>{c.title || (he ? 'ללא כותרת' : 'Untitled')}</option>)}
        </select>
        <button type="button" className="btn btn-sm" disabled={disabled || activeId == null}
                aria-label={he ? 'מחק שיחה' : 'Delete conversation'}
                onClick={() => onDelete(activeId)}>{he ? 'מחק' : 'Delete'}</button>
        {tasks && (
          <select className="select" value={activeTaskId == null ? '' : String(activeTaskId)}
                  aria-label={he ? 'משימות' : 'Tasks'}
                  onChange={(e) => { if (e.target.value !== '') onSelectTask(Number(e.target.value)); }}>
            <option value="">{he ? `— ${tasks.length} משימות —` : `— ${tasks.length} tasks —`}</option>
            {tasks.map((t) => <option key={t.id} value={String(t.id)}>{t.title}</option>)}
          </select>
        )}
      </div>
    </>
  );
}

// ---------- the page ----------
function ChatPage({
  lang, role, onOpenSettings, onOpenWorkbench, initialDraft, onDraftConsumed,
  initialTaskId, onTaskIdConsumed, onActivityChange, visible = true, companyPick = null,
}) {
  const he = lang === 'he';
  const [turns, setTurns] = useState([]);
  const [draft, setDraft] = useState('');
  // Consumed once: without the callback the same draft re-fills the
  // composer on every later visit to the chat page.
  useEffect(() => {
    if (initialDraft) { setDraft(initialDraft); onDraftConsumed && onDraftConsumed(); }
  }, [initialDraft]);
  const [sending, setSending] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [loading, setLoading] = useState(false);    // a selectConv GET is in flight
  const [convs, setConvs] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [companies, setCompanies] = useState(null);   // {tenant_id, envs, default}
  const [ownEnv, setEnv] = useState('');
  // Offline picker (spec 2026-11-02 §4.4): following the top bar, Talk is on `applied`, the pick it last
  // took; an answer streaming when the pick moves finishes, and is stored, in its own company first (R1).
  // While the picker loads (P16) Talk is on no company: nothing is listed, and nothing can be sent.
  const pending = pickPending(companyPick);
  const follows = !pending && !!companyPick && companies?.tenant_id === companyPick.tenantId;
  const [applied, setApplied] = useState(null);
  const env = pending ? '' : follows ? (applied ?? '') : ownEnv;
  const talk = talkEnv({ follows, pick: companyPick?.pick ?? null, sending, current: env });
  const dashboardScope = `${PCMAdapter.getToken()}:${companies?.tenant_id}:${env}`;
  const [dashboardOpen,setDashboardOpen] = useState(false);
  const [selectedDashboardId,setSelectedDashboardId] = useState(null);
  const [dashboardVersion,setDashboardVersion] = useState(0);
  const [pinArtifact,setPinArtifact] = useState(null);
  const pinResult = useCallback(artifact=>setPinArtifact(artifact),[]);
  useEffect(()=>{setSelectedDashboardId(null);setPinArtifact(null);setDashboardOpen(false);},[dashboardScope]);
  const envRef = useRef('');
  envRef.current = env;
  const [canvas, setCanvas] = useState(null);       // {turnIdx, partIdx, blockIdx} | null
  const openCanvas = useCallback((c) => { setActiveTaskId(null); setCanvas(c); }, []);
  const isAdmin = role === 'admin';
  const [tasks, setTasks] = useState(isAdmin ? [] : null);   // null = not offered
  const [tasksError, setTasksError] = useState('');
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [namedAdmin, setNamedAdmin] = useState(null);         // false = the admin secret
  useEffect(() => {
    if (!isAdmin) return;
    PCMAdapter.getMe().then((me) => setNamedAdmin(me.user_id != null)).catch(() => setNamedAdmin(null));
  }, [isAdmin]);
  const refreshTasks = useCallback(() => {
    if (!isAdmin) return;
    // The company being impersonated, as the chat's own tools see it (review M9). A malformed
    // or unscoped token must never fall through to an unfiltered, all-tenants list -- the route
    // treats a missing tenant_id as "every company" (review F3), so a null here is an error, not
    // an empty filter.
    const params = scopedTaskParams(PCMAdapter.getToken());
    if (!params) {
      setTasksError(he
        ? 'לא ניתן לקבוע איזו חברה מדומה כרגע; המשימות לא מוצגות.'
        : 'Could not tell which company this session is impersonating; tasks are not shown.');
      return;
    }
    PCMAdapter.getAgentTasks(params)
      .then((d) => { setTasks(d.tasks); setTasksError(''); })
      .catch((e) => setTasksError(String((e && (e.detail || e.message)) || e)));
  }, [isAdmin, he]);
  useEffect(() => {
    refreshTasks();
    if (!isAdmin) return undefined;
    const h = setInterval(refreshTasks, TASK_POLL_MS);
    return () => clearInterval(h);
  }, [refreshTasks, isAdmin]);
  // M3-D13 (#chat/task/<id>): select the task once the list HAS this id --
  // on a cold link `tasks` is still `[]` (the first refreshTasks has not
  // landed), so this re-checks whenever the list changes rather than only
  // on mount. A non-admin (`tasks` stays null, never offered) or a bad or
  // foreign id (never appears in `tasks`) is inert, not an error: the page
  // still opens on plain chat. `onTaskIdConsumed` is deliberately out of
  // the dependency array, matching initialDraft's effect one screen up --
  // App passes a fresh closure every render.
  useEffect(() => {
    if (initialTaskId == null || !tasks) return;
    if (tasks.some((t) => t.id === initialTaskId)) {
      setCanvas(null);
      setActiveTaskId(initialTaskId);
      onTaskIdConsumed && onTaskIdConsumed();
    }
  }, [initialTaskId, tasks]);
  const openRun = (tenantId, runId) => {
    // RUN_KEY, not a literal: the workbench page reads this exact key to reopen the run.
    try { localStorage.setItem(RUN_KEY, JSON.stringify({ tenant_id: tenantId, run_id: runId })); } catch (_) { /* no store */ }
    // Offline (spec §4.3): the run's own link, so the Workbench opens it and moves the pick to its company.
    if (companyPick?.pick) window.location.hash = `#workbench/${tenantId}/${runId}`;
    else if (onOpenWorkbench) onOpenWorkbench();
  };
  const activeTask = tasks && tasks.find((t) => t.id === activeTaskId);
  const historyRef = useRef([]);                    // wire history, as before
  const listRef = useRef(null);
  const followRef = useRef(true);
  const abortRef = useRef(null);
  const dirtyRef = useRef(false);                   // a stream ran since the last PUT
  const activeIdRef = useRef(null);                 // which conversation an in-flight PUT/GET was for
  // Backstop only: newChat/selectConv/send each set activeIdRef.current
  // SYNCHRONOUSLY, at the point they decide the new id, because this effect
  // alone lags setActiveId by a commit plus a passive-effect flush -- a
  // rejection landing inside that window compared against the stale id and
  // slipped past the guard below. Left in place in case a future call site
  // sets activeId without remembering to keep the ref in step.
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const stop = useCallback(() => { abortRef.current?.abort(); }, []);
  useEffect(() => {
    onActivityChange?.(sending ? { stop } : null);
  }, [sending, stop, onActivityChange]);
  useEffect(() => () => {
    const ac = abortRef.current;
    abortRef.current = null;
    dirtyRef.current = false;
    ac?.abort();
  }, []);
  useEffect(() => {
    if (listRef.current && followRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns, visible]);

  useEffect(() => {
    PCMAdapter.getChatCompanies().then((c) => {
      setCompanies(c);
      setEnv(pickEnv({ remembered: rememberedEnv(c.tenant_id), envs: c.envs, newestRunEnv: c.default }));
    }).catch(() => setCompanies({ tenant_id: null, envs: [], default: null }));
  }, []);

  const refreshList = useCallback(() => {
    // No company (not known yet, or the companies call failed): never fetch the
    // list unfiltered -- the server would answer with every company's conversations.
    if (companies === null) return;
    if (!env) { setConvs([]); return; }
    // A response for a company that is no longer the picked one is dropped.
    PCMAdapter.listConversations(100, env).then((list) => {
      if (envRef.current === env) setConvs(list);
    }).catch((err) => {
      if (envRef.current !== env) return;
      // Silent failure here reads as "my history is gone" (an empty rail)
      // and, since activeId is untouched, the user's next send opens a
      // FRESH conversation instead of continuing the one they meant to --
      // worse than the notice below.
      const detail = (err && (err.detail || err.message)) || String(err);
      setTurns((ts) => [...ts, { role: 'notice', kind: 'error', detail: he ? `טעינת רשימת השיחות נכשלה: ${detail}` : `Could not load the conversation list: ${detail}` }]);
    });
  }, [he, env, companies]);
  useEffect(() => { refreshList(); }, [refreshList]);

  // Persist when a stream has just closed: `sending` flipping to false is
  // the one moment the committed `turns` state is complete. Fire-and-forget
  // -- a failed save shows a notice and never blocks the next send.
  useEffect(() => {
    if (sending || !dirtyRef.current || activeId == null) return;
    dirtyRef.current = false;
    const cid = activeId;
    const title = titleFrom((turns.find((t) => t.role === 'user') || {}).text || '');
    PCMAdapter.putConversation(cid, { title, turns })
      .then(refreshList)
      .catch((err) => {
        // The user may have selected another conversation while this PUT
        // was in flight; its notice must not land in that one's turns.
        if (activeIdRef.current !== cid) return;
        const detail = (err && (err.detail || err.message)) || String(err);
        setTurns((ts) => [...ts, { role: 'notice', kind: 'error', detail: he ? `השמירה נכשלה: ${detail}` : `Save failed: ${detail}` }]);
      });
  }, [sending]);   // eslint-disable-line react-hooks/exhaustive-deps

  const newChat = ({ keepDraft = false } = {}) => {
    if (sending) return;
    followRef.current = true;
    setAnnouncement('');
    activeIdRef.current = null;   // synchronous -- see the comment on the ref above
    setActiveId(null); setTurns([]); historyRef.current = []; setCanvas(null);
    if (!keepDraft) setDraft('');
    // Cancels the RELEVANCE of any selectConv GET still in flight -- its own
    // guard (activeIdRef.current !== id) will now skip populating turns, but
    // that guard is exactly why it will never reach its own setLoading(false)
    // either. Clear it here or a load abandoned via "New chat" leaves
    // `loading` stuck true forever, permanently disabling send().
    setLoading(false);
  };

  // Take the pick once no answer streams. The first pick keeps the draft (one handed in by initialDraft); a
  // draft typed for one company never carries to the next. selectConv sets `applied` with the pick it moves.
  useEffect(() => {
    if (!follows || talk.waiting) return;
    const next = talk.env ?? '';
    if (next === applied) return;
    setApplied(next); setConvs([]); newChat({ keepDraft: applied === null });
  }, [follows, talk.env, talk.waiting]);   // eslint-disable-line react-hooks/exhaustive-deps

  // activeIdRef is set SYNCHRONOUSLY, before the GET, and both the success
  // and the failure branch are guarded on it still matching `id`. Two
  // things this buys together: (1) the turn/history state is switched away
  // from the OLD conversation immediately, so a failure notice can never
  // land in -- and later get PUT into -- the wrong conversation's stored
  // transcript (it used to: switching to a conversation whose GET 404s
  // left `turns` untouched, i.e. still the previous conversation's, and
  // the notice got appended there); (2) clicking a second conversation
  // before the first one's GET resolves makes the first GET's guard fail,
  // so an out-of-order response can never clobber the one the user is
  // actually looking at now.
  //
  // `loading` is true from here until the GET settles, and gates send()
  // (below) and the composer. Without it, sending while a GET for THIS SAME
  // id is still in flight passed every existing guard (id === activeId,
  // activeIdRef.current === id) because nothing about those guards concerns
  // a load in progress: the GET would land, setTurns(loaded) would discard
  // the question the user just typed and appended, and the following SSE
  // delta would write into one of the loaded turns at the same index --
  // corrupting a turn that was never the one being streamed into, or
  // throwing inside a setTurns updater with no error boundary anywhere in
  // this directory to catch it. Clearing `loading` is itself guarded by the
  // same activeIdRef check as the turns update, for the identical reason:
  // an abandoned (superseded) GET's own settle must not clear the CURRENT
  // load's `loading` state out from under it.
  const selectConv = (id) => {
    if (sending || id === activeId) return;
    followRef.current = true;
    setAnnouncement('');
    activeIdRef.current = id;
    setActiveId(id);
    setTurns([]);
    historyRef.current = [];
    setCanvas(null);
    setLoading(true);
    PCMAdapter.getConversation(id).then((c) => {
      if (activeIdRef.current !== id) return;
      setLoading(false);
      if (follows) {
        // Spec §4.3: another company's conversation moves the pick, in this same commit as its turns.
        const choice = c.env && c.env !== env && choiceByMirrorEnv(companyPick.companies, c.env);
        if (choice) { companyPick.choose(choice); setApplied(c.env); }
      } else if (c.env && c.env !== env) setEnv(c.env);
      const loaded = Array.isArray(c.turns) ? c.turns : [];
      setTurns(loaded);
      historyRef.current = wireHistory(loaded);
    }).catch((err) => {
      if (activeIdRef.current !== id) return;
      setLoading(false);
      const detail = (err && (err.detail || err.message)) || String(err);
      setTurns((ts) => [...ts, { role: 'notice', kind: 'error', detail }]);
    });
  };

  const deleteConv = async (id) => {
    if (!await confirmAction(he ? 'למחוק את השיחה?' : 'Delete this conversation?', { lang, danger: true, confirmLabel: he ? 'מחיקה' : 'Delete' })) return;
    setDeleteError('');
    PCMAdapter.deleteConversation(id).then(() => {
      if (id === activeIdRef.current) newChat();
      refreshList();
    }).catch((err) => {
      const detail = (err && (err.detail || err.message)) || String(err);
      const msg = he ? `מחיקת השיחה נכשלה: ${detail}` : `Could not delete the conversation: ${detail}`;
      // Same corruption shape as selectConv: `turns` is exactly what the
      // next send PUTs, so a failure notice for a conversation the user is
      // NOT looking at must not be written into whatever conversation IS
      // active. A failure for another conversation is shown outside the
      // transcript, so the next save cannot attach it to the wrong chat.
      if (activeIdRef.current === id) {
        setTurns((ts) => [...ts, { role: 'notice', kind: 'error', detail: msg }]);
      } else {
        setDeleteError(msg);
      }
    });
  };

  const send = async () => {
    const text = draft.trim();
    // `loading`: see the comment on selectConv -- a send while THIS
    // conversation's own GET is still in flight is exactly the race that
    // corrupts the transcript, not just a wasted request.
    if (!text || sending || abortRef.current || loading) return;
    // A new conversation needs a picked company: with none, the server would
    // choose its default one, which the picker never showed.
    if (activeId == null && !env) return;
    followRef.current = true;
    setDraft('');
    setSending(true);
    setAnnouncement(he ? 'הסוכן מכין תשובה…' : 'The agent is preparing a reply…');
    dirtyRef.current = true;

    const ac = new AbortController();
    abortRef.current = ac;
    const historyBeforeSend = historyRef.current;
    historyRef.current = [...historyBeforeSend, { role: 'user', content: text }];
    const assistantIdx = { current: null };
    setTurns((ts) => {
      const next = [...ts, { role: 'user', text }, { role: 'assistant', parts: [], pending: true, streaming: true }];
      assistantIdx.current = next.length - 1;
      return next;
    });

    // Keep old turn identities so memoized historical answers are not reparsed per delta.
    const appendToAssistant = (updater) => {
      setTurns((ts) => {
        const idx = assistantIdx.current;
        if (!ts[idx]) return ts;
        const next = ts.slice();
        next[idx] = updater(next[idx]);
        return next;
      });
    };
    const onEvent = (ev) => {
      if (abortRef.current !== ac || ac.signal.aborted) return;
      if (CHAT_EVENT_KINDS.has(ev.type)) {
        appendToAssistant((turn) => ac.signal.aborted ? turn : applyChatEvent(turn, ev));
      }
    };

    const finish = () => {
      const stopped = ac.signal.aborted;
      appendToAssistant((turn) => {
        const assembled = assistantContent(turn.parts, assistantIdx.current);
        if (assembled) historyRef.current = [...historyRef.current, { role: 'assistant', content: assembled }];
        else if (stopped) historyRef.current = historyBeforeSend;
        return { ...turn, streaming: false, pending: false, ...(stopped ? { stopped: true } : {}) };
      });
      setAnnouncement(stopped ? (he ? 'התשובה נעצרה.' : 'Reply stopped.') : (he ? 'התשובה מוכנה.' : 'Reply ready.'));
    };

    let cid = activeId;
    try {
      if (cid == null) {
        // Finish creation even after Stop: its ID is needed to save the stopped question.
        cid = (await PCMAdapter.createConversation('', null, env)).id;
        if (abortRef.current !== ac) return;
        activeIdRef.current = cid;
        setActiveId(cid);
      }
      if (!ac.signal.aborted) {
        await PCMAdapter.postTalkStream(historyRef.current, { onEvent, signal: ac.signal, conversationId: cid, selectedDashboardId });
      }
      if (abortRef.current !== ac) return;
      finish();
      if (!ac.signal.aborted) setDashboardVersion(version=>version+1);
    } catch (err) {
      if (abortRef.current !== ac) return;
      if (ac.signal.aborted) {
        finish();
        if (cid == null) {
          const detail = (err && (err.detail || err.message)) || String(err);
          setTurns((ts) => [...ts, { role: 'notice', kind: 'error', detail: he
            ? `לא ניתן לשמור את התשובה שנעצרה: ${detail}`
            : `Could not save the stopped reply: ${detail}` }]);
        }
      } else {
        const status = err && err.status;
        const detail = (err && (err.detail || err.message)) || String(err);
        setTurns((ts) => {
          const next = ts.slice();
          const turn = next[assistantIdx.current];
          if (turn && turn.parts.length > 0) {
            const assembled = assistantContent(turn.parts, assistantIdx.current);
            if (assembled) historyRef.current = [...historyRef.current, { role: 'assistant', content: assembled }];
            next[assistantIdx.current] = { ...turn, streaming: false, pending: false };
          } else {
            historyRef.current = historyBeforeSend;
            if (turn) next.splice(assistantIdx.current, 1);
            if (cid == null) next.splice(assistantIdx.current - 1, 1);
          }
          next.push({ role: 'notice', kind: status === 400 ? 'nokey' : 'error', detail: String(detail) });
          return next;
        });
        if (cid == null) setDraft(text);
        setAnnouncement(he ? 'הבקשה נכשלה. בדקו את ההודעה בשיחה.' : 'The request failed. Review the message in the conversation.');
      }
    } finally {
      if (abortRef.current === ac) {
        abortRef.current = null;
        setSending(false);
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && e.keyCode !== 229) { e.preventDefault(); send(); }
  };

  // The canvas re-derives its block from live state, so a chart that
  // re-rendered in the message is the same chart in the panel.
  const canvasPart = canvas && turns[canvas.turnIdx]?.parts?.[canvas.partIdx];
  const heldChoice = talk.waiting && choiceByMirrorEnv(companyPick.companies, env);
  const placeLabel = heldChoice ? choiceLabel(heldChoice, he) : env;
  // The note already says there is no upload: the label without choiceLabel's own 'no upload yet'.
  const pickLabel = companyPick?.pick ? choiceLabel({ ...companyPick.pick, noUpload: false }, he) : '';
  const canvasTok = useMemo(() => canvasPart
    ? (canvas.artifact && canvasPart.artifact
      ? { type: 'query-result', artifact: {...canvasPart.artifact, ...canvas.presentation} }
      : blockAt(canvasPart.text || '', canvas.blockIdx))
    : null, [canvas, canvasPart]);

  return (
    <>
      <div className="page-head chat-page-head">
        <h1 className="page-title">{he ? 'שיחה עם הנתונים' : 'Talk to your data'}</h1>
        <div className="page-actions">
          <button type="button" className="btn" aria-expanded={dashboardOpen} disabled={!env} onClick={()=>setDashboardOpen(open=>!open)}>{he?'לוחות נתונים':'Dashboards'}</button>
          {talk.waiting && <span role="status" dir="auto">{he
            ? `מסיים את התשובה ב-${placeLabel}; עובר אחריה` : `Finishing the answer in ${placeLabel}; switching after it`}</span>}
          {talk.noUpload && companyPick.pick && <span role="status" dir="auto">{he
            ? `אין העלאה עדיין ל-${pickLabel}` : `No upload yet for ${pickLabel}`}</span>}
          {!follows && <>
          <label className="sr-only" htmlFor="chat-company">{he ? 'חברה' : 'Company'}</label>
          <select id="chat-company" className="input" dir="ltr" value={env}
                  disabled={sending || activeId != null || !companies || companies.envs.length < 2}
                  onChange={(e) => { setEnv(e.target.value); setConvs([]); if (companies) rememberEnv(companies.tenant_id, e.target.value); }}>
            {(companies ? companies.envs : []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          </>}
        </div>
      </div>
      {env && <div data-dashboards-surface hidden={!dashboardOpen}><DataDashboards key={dashboardScope} lang={lang} env={env} scopeKey={dashboardScope}
        onSelectDashboard={setSelectedDashboardId} selectedDashboardId={selectedDashboardId} refreshVersion={dashboardVersion} visible={dashboardOpen}/></div>}
      {pinArtifact && !talk.noUpload && <SaveToDashboard key={dashboardScope} artifact={pinArtifact} lang={lang} env={env} scopeKey={dashboardScope} onSaved={board=>{
        setPinArtifact(null);if(board){setSelectedDashboardId(board.id);setDashboardOpen(true);setDashboardVersion(version=>version+1);}
      }}/>}
      <div className={chatCls('chat-grid', (canvasTok || activeTask) && 'has-canvas')}>
        <ConvRail convs={convs} activeId={activeId} disabled={sending} lang={lang} noCompany={!pending && companies !== null && !env}
                  onSelect={selectConv} onNew={newChat} onDelete={deleteConv}
                  tasks={tasks} tasksError={tasksError} activeTaskId={activeTaskId} canCreate={namedAdmin !== false}
                  onSelectTask={(id) => { setCanvas(null); setActiveTaskId(id); }}/>
        <div className="card chat-card">
          {deleteError && <div role="alert" className="set-error" dir="auto">{deleteError}</div>}
          <div className="chat-messages" ref={listRef} onScroll={(e) => {
            const el = e.currentTarget;
            followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
          }}>
            {turns.length === 0 && (
              loading ? (
                // Reuses .chat-empty-title rather than a new class: it
                // already lives in this exact container for centered status
                // copy ("Ask something..."), so this needs no new CSS rule
                // and check_css_classes.py has nothing new to see. Without
                // this branch a slow GET showed the full empty state --
                // title, suggestion cards, a live composer -- which reads
                // as "New chat" with no sign anything is loading.
                <div className="chat-empty">
                  <div className="chat-empty-title">{he ? 'טוען…' : 'Loading…'}</div>
                </div>
              ) : (
                <div className="chat-empty">
                  <div className="chat-empty-title">{he ? 'שאלו משהו על הנתונים שלכם.' : 'Ask something about your data.'}</div>
                  <div className="chat-suggest-grid">
                    {SUGGESTIONS.map(([en, hebrew, color]) => (
                      <button type="button" key={en} className="chat-suggest" dir="auto" disabled={sending || (activeId == null && !env)}
                              style={{ borderInlineStartColor: color }} onClick={() => setDraft(he ? hebrew : en)}>
                        {he ? hebrew : en}
                      </button>
                    ))}
                  </div>
                  <div className="chat-note">
                    {he
                      ? 'התשובות מבוססות על המראה בלבד. כל שאילתת SQL שהעוזר מריץ מוצגת במלואה.'
                      : 'Answers come from the mirror only. Every SQL query the assistant runs is shown in full.'}
                  </div>
                </div>
              )
            )}
            {turns.map((turn, i) => (
              <ChatMessage turn={turn} turnIdx={i} lang={lang} role={role} onOpenSettings={onOpenSettings}
                           onOpen={openCanvas} onPin={pinResult} key={i}/>
            ))}
          </div>
          <p className="sr-only" role="status" aria-atomic="true">{announcement}</p>
          <div className="chat-composer">
            <textarea className="textarea chat-input" rows={1} value={draft} readOnly={sending || loading || talk.noUpload}
                      aria-label={he ? 'שאלה לנתונים' : 'Question about your data'}
                      placeholder={he ? 'שאלו על הנתונים… Enter לשליחה, Shift+Enter לשורה חדשה' : 'Ask about your data… Enter to send, Shift+Enter for a new line'}
                      onChange={(e) => setDraft(e.target.value)} onKeyDown={onKeyDown}/>
            <button type="button" className="btn primary" disabled={!sending && (loading || !draft.trim() || (activeId == null && !env))} onClick={sending ? stop : send}>
              {sending ? (he ? 'עצור' : 'Stop') : (he ? 'שלח' : 'Send')}
            </button>
          </div>
        </div>
        {activeTask ? (
          <Canvas title={activeTask.title} lang={lang} onClose={() => setActiveTaskId(null)}>
            {/* key={activeTask.id}: without it, switching tasks reuses the same TaskPanel
                instance and a stale getAgentTask response for the PREVIOUS task can resolve
                after the switch and paint under the new task's heading (review F2). Keying on
                the id remounts on every switch, so a stale .then targets a discarded instance --
                the same shape as ApprovalCard's `alive` flag and WorkbenchPage's tenantRef
                check elsewhere in this diff. */}
            <TaskPanel key={activeTask.id} taskId={activeTask.id} tenantId={activeTask.tenant_id}
                       lang={lang} onChanged={refreshTasks} onOpenRun={openRun}/>
          </Canvas>
        ) : canvasTok && (
          <Canvas title={canvasTitle(canvasTok, lang)} lang={lang} onClose={() => setCanvas(null)}>
            <CanvasBlock tok={canvasTok} lang={lang}/>
          </Canvas>
        )}
      </div>
    </>
  );
}

window.ChatPage = ChatPage;
